import { useMemo, useEffect } from 'react';
import { useAction, useQuery } from 'convex/react';
import { useUser } from '@clerk/clerk-react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { api } from '../convex';
import { setSubscriptionStatus } from '../store/slices/subscriptionSlice';

// Map Convex subscription status to our Redux slice structure
function mapConvexStatusToRedux(data) {
  if (!data) return null;
  const now = Date.now();
  const status = data.status || 'inactive';
  const billingCycle = data.billingCycle || 'monthly';
  const currentPeriodEnd = data.currentPeriodEnd || null;

  let daysUntilExpiry = null;
  if (typeof currentPeriodEnd === 'number') {
    daysUntilExpiry = Math.floor((currentPeriodEnd - now) / (1000 * 60 * 60 * 24));
  }

  // Use the payment_status directly from Convex backend and map to frontend expectations
  let payment_status = 'unknown';
  
  // Map Convex payment_status to frontend expected values
  if (data.payment_status) {
    switch (data.payment_status) {
      case 'active':
        payment_status = 'current'; // Map 'active' to 'current' for UI display
        break;
      case 'expiring_soon':
        payment_status = 'expired'; // Map 'expiring_soon' to 'expired' warning state  
        break;
      case 'cancelled':
        payment_status = 'cancelled';
        break;
      case 'none':
        payment_status = 'unknown';
        break;
      default:
        payment_status = data.payment_status; // Pass through other statuses
    }
  }

  let expires_soon = false;
  const has_access_base = data.hasSubscription && (status === 'active' || status === 'trialing');
  const notExpired = daysUntilExpiry === null || daysUntilExpiry > 0;

  expires_soon = daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 7;
  const has_access = has_access_base && notExpired;

  return {
    status,
    has_access,
    plan_type: billingCycle,
    plan_name: billingCycle === 'yearly' ? 'Yearly Plan' : 'Monthly Plan',
    current_period_end: currentPeriodEnd ? new Date(currentPeriodEnd).toISOString() : null,
    days_until_expiry: daysUntilExpiry,
    expires_soon,
    payment_status,
    renewal_required: !has_access,
    is_active: has_access && (status === 'active' || status === 'trialing'),
    // passthroughs
    product_id: data.productId,
    product_name: data.productName,
    amount: data.amount,
    currency: data.currency,
    source: 'convex',
  };
}

// Returns a function that creates a checkout via Convex action
export function useCreateConvexCheckout() {
  const { user } = useUser();
  
  // Always called unconditionally — no try-catch around hooks
  const createCheckout = useAction(api.actions.payments.createCheckout);

  const handler = useMemo(() => {
    return async (productType = 'monthly') => {
      if (!user) throw new Error('User not signed in');

      const successUrl = window.location.origin + window.location.pathname + '?payment_success=true';
      const cancelUrl = window.location.origin + window.location.pathname + '?payment_success=false';

      // Always use the user's email on first attempt so Polar checkout shows their real email
      let customerEmail = user.emailAddresses?.[0]?.emailAddress;
      // If Polar rejects the domain, we will retry with a fallback inside the catch handler (no pre-emptive override)

      try {
        const result = await createCheckout({
          userId: user.id,
          productType,
          successUrl,
          cancelUrl,
          customerEmail,
          customerName: user.fullName || user.firstName || undefined,
        });

        // Handle upgrade scenarios with special messaging
        if (result.success && result.isUpgrade) {
          toast.success(`Upgrading from ${result.previousPlan} to ${result.newPlan} plan...`);
        }

        return result;
      } catch (error) {
        // Enhanced error handling and targeted retries
        const rawMsg = String(error?.message || error || '');
        console.error('🚨 [Payment] Checkout error:', rawMsg);

        // 1) Handle Convex transient connection loss (observed error)
        if (/Connection lost while action was in flight/i.test(rawMsg) || /(ECONN|ENOTFOUND|timed out|502|503|504)/i.test(rawMsg)) {
          toast('Network hiccup detected. Retrying checkout...');
          try {
            // brief backoff
            await new Promise(r => setTimeout(r, 600));
            const retry = await createCheckout({
              userId: user.id,
              productType,
              successUrl,
              cancelUrl,
              customerEmail,
              customerName: user.fullName || user.firstName || undefined,
            });
            return retry;
          } catch (retryErr) {
            const retryMsg = String(retryErr?.message || retryErr || '');
            console.error('🚨 [Payment] Retry failed:', retryMsg);
            throw new Error('Temporary connection issue. Please try again.');
          }
        }
        
        // 2) Polar email domain validation failures
        if (rawMsg.includes('domain name') && rawMsg.includes('does not exist')) {
          console.log('🔧 [Payment] Email domain validation failed, retrying with fallback email...');
          try {
            const fallbackEmail = `user.${user.id.slice(-8)}@gmail.com`;
            const retryResult = await createCheckout({
              userId: user.id,
              productType,
              successUrl,
              cancelUrl,
              customerEmail: fallbackEmail,
              customerName: user.fullName || user.firstName || undefined,
            });
            return retryResult;
          } catch (retryError) {
            console.error('🚨 [Payment] Retry also failed:', retryError);
            throw new Error('Payment processing failed. Please try again or contact support if the issue persists.');
          }
        }
        
        // 3) Common error scenarios with user-friendly messages
        if (rawMsg.includes('authentication')) {
          throw new Error('Authentication failed. Please sign out and sign back in.');
        } else if (rawMsg.includes('network')) {
          throw new Error('Network error. Please check your connection and try again.');
        } else if (rawMsg.includes('product') || rawMsg.includes('Product ID not configured')) {
          throw new Error('Subscription plan not available. Please contact support.');
        } else {
          throw new Error(rawMsg || 'Failed to create checkout session. Please try again.');
        }
      }
    };
  }, [createCheckout, user]);

  return handler;
}

// New: open customer portal via Convex action
export function useOpenCustomerPortal() {
  const { user } = useUser();

  // Always called unconditionally — no try-catch around hooks
  const generatePortal = useAction(api.actions.payments.generatePortalUrl);

  return useMemo(() => {
    return async (returnUrl) => {
      if (!user) throw new Error('User not signed in');

      const url = returnUrl || (window.location.origin + '/pricing');
      const res = await generatePortal({ userId: user.id, returnUrl: url });
      if (res?.success && res.portalUrl) {
        try {
          // Set a local flag so we can auto-sync when the user returns
          const payload = { at: Date.now(), reason: 'portal' };
          localStorage.setItem('portal_sync_pending', JSON.stringify(payload));
        } catch {}
        window.location.href = res.portalUrl;
        return res;
      }
      throw new Error(res?.error || 'Failed to start customer portal');
    };
  }, [user, generatePortal]);
}

// Reactively sync Convex subscription status into Redux
export function useConvexSubscriptionSync(autoDispatch = true) {
  const { user } = useUser();
  const dispatch = useDispatch();

  // Always called unconditionally — no try-catch around hooks
  const raw = useQuery(api.payments.getUserSubscriptionStatus, user ? { userId: user.id } : 'skip');
  
  const mapped = raw ? mapConvexStatusToRedux(raw) : null;

  useEffect(() => {
    if (autoDispatch && mapped) {
      dispatch(setSubscriptionStatus(mapped));
    }
  }, [autoDispatch, mapped, dispatch]);

  return { raw, mapped };
}

export default {
  useCreateConvexCheckout,
  useConvexSubscriptionSync,
  useOpenCustomerPortal,
};